/**
 * Shopify Theme Wishlist System
 * Stores wishlisted product handles in localStorage and synchronizes UI states.
 */

const WISHLIST_KEY = 'shopify-theme-wishlist';

const Wishlist = {
  get() {
    try {
      const list = localStorage.getItem(WISHLIST_KEY);
      return list ? JSON.parse(list) : [];
    } catch (e) {
      console.error('Error reading wishlist from localStorage:', e);
      return [];
    }
  },

  set(list) {
    try {
      localStorage.setItem(WISHLIST_KEY, JSON.stringify(list));
      // Dispatch custom event to notify other scripts of change
      document.dispatchEvent(new CustomEvent('wishlist:updated', { detail: { wishlist: list } }));
    } catch (e) {
      console.error('Error writing wishlist to localStorage:', e);
    }
  },

  add(handle) {
    const list = this.get();
    if (!list.includes(handle)) {
      list.push(handle);
      this.set(list);
    }
  },

  remove(handle) {
    let list = this.get();
    list = list.filter(item => item !== handle);
    this.set(list);
  },

  toggle(handle) {
    if (this.isIn(handle)) {
      this.remove(handle);
    } else {
      this.add(handle);
    }
  },

  isIn(handle) {
    return this.get().includes(handle);
  },

  updateButtons() {
    const buttons = document.querySelectorAll('[data-wishlist-button]');
    const list = this.get();
    
    buttons.forEach(btn => {
      const handle = btn.getAttribute('data-product-handle');
      if (!handle) return;
      
      const inWishlist = list.includes(handle);
      const emptyIcon = btn.querySelector('.icon-wishlist-empty');
      const filledIcon = btn.querySelector('.icon-wishlist-filled');
      
      if (inWishlist) {
        btn.setAttribute('aria-label', 'Remove from wishlist');
        btn.setAttribute('aria-pressed', 'true');
        btn.classList.add('is-active');
        if (emptyIcon) emptyIcon.classList.add('hidden');
        if (filledIcon) filledIcon.classList.remove('hidden');
      } else {
        btn.setAttribute('aria-label', 'Add to wishlist');
        btn.setAttribute('aria-pressed', 'false');
        btn.classList.remove('is-active');
        if (emptyIcon) emptyIcon.classList.remove('hidden');
        if (filledIcon) filledIcon.classList.add('hidden');
      }
    });
  },

  updateCounters() {
    const count = this.get().length;
    
    // Header count badges
    const badges = document.querySelectorAll('.wishlist-count-badge');
    badges.forEach(badge => {
      badge.textContent = count;
      if (count > 0) {
        badge.classList.remove('hidden');
      } else {
        badge.classList.add('hidden');
      }
    });
    
    // Mobile menu text counts
    const mobileCounts = document.querySelectorAll('.wishlist-mobile-count');
    mobileCounts.forEach(el => {
      el.textContent = count;
    });
  },

  initButtons() {
    // Add event delegation on body for click events on wishlist buttons
    document.body.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-wishlist-button]');
      if (!btn) return;
      
      e.preventDefault();
      e.stopPropagation();
      
      const handle = btn.getAttribute('data-product-handle');
      if (handle) {
        this.toggle(handle);
      }
    });
  },

  initWishlistPage() {
    const grid = document.querySelector('[data-wishlist-grid]');
    const emptyState = document.getElementById('wishlist-empty-state');
    if (!grid) return;

    const renderPage = () => {
      const list = this.get();
      if (list.length === 0) {
        grid.innerHTML = '';
        grid.classList.add('hidden');
        if (emptyState) emptyState.classList.remove('hidden');
        return;
      }

      if (emptyState) emptyState.classList.add('hidden');
      grid.classList.remove('hidden');
      grid.innerHTML = '<div class="wishlist-loading"><div class="loading-spinner"></div></div>';

      const promises = list.map(handle => 
        fetch(`${window.shopUrl || ''}/products/${handle}?view=card`)
          .then(res => res.ok ? res.text() : null)
          .catch(err => {
            console.error(`Error fetching card for ${handle}:`, err);
            return null;
          })
      );

      Promise.all(promises).then(cardsHtml => {
        const validCards = cardsHtml.filter(html => html && html.trim() !== '');
        
        if (validCards.length === 0) {
          grid.innerHTML = '';
          grid.classList.add('hidden');
          if (emptyState) emptyState.classList.remove('hidden');
          return;
        }

        grid.innerHTML = '';
        validCards.forEach(html => {
          const wrapper = document.createElement('div');
          wrapper.className = 'grid__item';
          wrapper.innerHTML = html;
          grid.appendChild(wrapper);
        });

        // Sync buttons on the wishlist page
        this.updateButtons();

        // Wire up instant fade-out removal for wishlist page buttons
        grid.querySelectorAll('[data-wishlist-button]').forEach(btn => {
          btn.addEventListener('click', (e) => {
            const handle = btn.getAttribute('data-product-handle');
            if (!handle) return;
            
            // Find target item wrapper
            const cardItem = btn.closest('.grid__item') || btn.closest('product-card');
            if (cardItem) {
              cardItem.style.transition = 'opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1), transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)';
              cardItem.style.opacity = '0';
              cardItem.style.transform = 'scale(0.9)';
              setTimeout(() => {
                cardItem.remove();
                if (this.get().length === 0) {
                  grid.classList.add('hidden');
                  if (emptyState) emptyState.classList.remove('hidden');
                }
              }, 300);
            }
          });
        });
      });
    };

    renderPage();
  },

  init() {
    this.initButtons();
    this.updateButtons();
    this.updateCounters();
    
    // Listen to our own updates to keep page components in sync
    document.addEventListener('wishlist:updated', () => {
      this.updateButtons();
      this.updateCounters();
    });
    
    // Initialize wishlist page if on it
    this.initWishlistPage();
  }
};

document.addEventListener('DOMContentLoaded', () => {
  Wishlist.init();
});

// Expose Wishlist globally
window.Wishlist = Wishlist;
